import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DataTable } from "@/components/common/DataTable";
import { UserDialog } from "@/components/users/UserDialog";
import { ResetPasswordDialog } from "@/components/users/PasswordDialogs";
import { Plus, Pencil, Trash2, KeyRound, UserX, UserCheck } from "lucide-react";
import { toast } from "sonner";

const PERM_CLS = { edit: "bg-emerald-100 text-emerald-800", view: "bg-sky-100 text-sky-800" };
const SHORT = { dashboard: "dashboard", masters: "master_data", mappings: "mappings", stock: "inventory", shipments: "shipments", purchasing: "purchasing", reports: "reports", audit: "audit_log" };

const PermBadges = ({ perms, t }) => (
  <div className="flex flex-wrap gap-1 max-w-[420px]">
    {Object.entries(perms).filter(([, l]) => l !== "none").map(([m, l]) => (
      <span key={m} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${PERM_CLS[l]}`}>{t(SHORT[m])} · {t(`perm_${l}`)}</span>
    ))}
  </div>
);

export default function Users() {
  const { t } = useLang();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const [pwTarget, setPwTarget] = useState(null);

  const load = async () => {
    const [u, m] = await Promise.all([api.get("/users"), api.get("/users/meta")]);
    setUsers(u.data);
    setMeta(m.data);
  };
  useEffect(() => { load(); }, []);

  const run = async (fn, msg = t("saved")) => {
    try { await fn(); toast.success(msg); load(); return true; } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const save = async (f) => {
    const body = { name: f.name, email: f.email, role: f.role, active: f.active, permissions: f.role === "admin" ? null : f.permissions };
    const ok = await run(() => (f.id ? api.put(`/users/${f.id}`, body) : api.post("/users", { ...body, password: f.password })));
    if (ok) setEdit(undefined);
  };
  const toggleActive = (u) => run(() => api.put(`/users/${u.id}`, { ...u, active: !u.active, permissions: u.custom_permissions ? u.permissions : null }));
  const remove = (u) => window.confirm(`${t("confirm_delete")} ${u.email}`) && run(() => api.delete(`/users/${u.id}`));

  const columns = [
    { key: "name", label: t("name"), className: "font-medium whitespace-nowrap" },
    { key: "email", label: t("email"), className: "font-mono text-xs" },
    { key: "role", label: t("role"), value: (u) => t(`role_${u.role}`) },
    { key: "active", label: t("status"), value: (u) => (u.active ? t("active") : t("inactive")),
      render: (u) => <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`} data-testid={`user-status-${u.email}`}>{u.active ? t("active") : t("inactive")}</span> },
    { key: "permissions", label: t("permissions"), filter: false, sort: false,
      value: (u) => Object.entries(u.permissions).filter(([, l]) => l !== "none").map(([m, l]) => `${t(SHORT[m])}: ${t(`perm_${l}`)}`).join(", "),
      render: (u) => <div>{u.custom_permissions && <span className="text-[10px] uppercase text-amber-700 font-semibold">{t("custom")}</span>}<PermBadges perms={u.permissions} t={t} /></div> },
    { key: "actions", label: "", filter: false, sort: false, render: (u) => (
      <div className="flex justify-end whitespace-nowrap">
        <Button variant="ghost" size="sm" onClick={() => setEdit(u)} title={t("edit")} data-testid={`user-edit-${u.email}`}><Pencil size={14} /></Button>
        <Button variant="ghost" size="sm" onClick={() => setPwTarget(u)} title={t("reset_password")} data-testid={`user-reset-pw-${u.email}`}><KeyRound size={14} /></Button>
        {u.id !== me?.id && <>
          <Button variant="ghost" size="sm" onClick={() => toggleActive(u)} title={u.active ? t("deactivate") : t("activate")} data-testid={`user-toggle-active-${u.email}`}>
            {u.active ? <UserX size={14} /> : <UserCheck size={14} />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => remove(u)} className="text-red-600" title={t("delete")} data-testid={`user-delete-${u.email}`}><Trash2 size={14} /></Button>
        </>}
      </div>
    ) },
  ];

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("users_mgmt")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("users_subtitle")}</p>
        </div>
        <Button onClick={() => setEdit(null)} disabled={!meta} className="bg-slate-900 hover:bg-slate-800" data-testid="user-add-btn">
          <Plus size={14} className="mr-2" />{t("add")}
        </Button>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <DataTable columns={columns} rows={users} testid="users-table" />
        </CardContent>
      </Card>
      {meta && <UserDialog open={edit !== undefined} initial={edit} meta={meta} onClose={() => setEdit(undefined)} onSave={save} />}
      <ResetPasswordDialog target={pwTarget} onClose={() => setPwTarget(null)} />
    </div>
  );
}
